// src/components/admin/page-management/section-forms/CtaForm.js
import React from 'react';
import ColorPicker from '../../shared/ColorPicker';

const CtaForm = ({ content, updateContent }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Call to Action Section</h3>
      
      {/* Main Content Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Content</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Title</label>
          <input
            type="text"
            value={content?.title || 'Ready to Elevate Your Brand?'}
            onChange={(e) => updateContent('title', e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={content?.description || 'Join hundreds of successful businesses who trust us with their packaging needs. Get started with your custom solution today.'}
            onChange={(e) => updateContent('description', e.target.value)}
            className="w-full p-2 border rounded"
            rows="3"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Text Color</label>
          <ColorPicker
            color={content?.textColor || '#ffffff'}
            onChange={(color) => updateContent('textColor', color)}
          />
        </div>
      </div>
      
      {/* Background Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Background</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Background Color</label>
          <ColorPicker
            color={content?.backgroundColor || '#013263'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.gradientEnabled !== false}
              onChange={(e) => updateContent('gradientEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Gradient Background</span>
          </label>
        </div>
        
        {content?.gradientEnabled !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Gradient Direction</label>
            <select
              value={content?.gradientDirection || 'to right'}
              onChange={(e) => updateContent('gradientDirection', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="to right">Left to Right</option>
              <option value="to left">Right to Left</option>
              <option value="to bottom">Top to Bottom</option>
              <option value="to top">Bottom to Top</option>
              <option value="to bottom right">Top Left to Bottom Right</option>
              <option value="to bottom left">Top Right to Bottom Left</option>
              <option value="to top right">Bottom Left to Top Right</option>
              <option value="to top left">Bottom Right to Top Left</option>
            </select>
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showBackgroundEffects !== false}
              onChange={(e) => updateContent('showBackgroundEffects', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Gradient Overlay Effects</span>
          </label>
        </div>
      </div>
      
      {/* Primary Button Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Primary Button</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showPrimaryButton !== false}
              onChange={(e) => updateContent('showPrimaryButton', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Primary Button</span>
          </label>
        </div>
        
        {content?.showPrimaryButton !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <input
                type="text"
                value={content?.primaryButtonText || 'Browse Products'}
                onChange={(e) => updateContent('primaryButtonText', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Link</label>
              <input
                type="text"
                value={content?.primaryButtonLink || '/products'}
                onChange={(e) => updateContent('primaryButtonLink', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Color</label>
                <ColorPicker
                  color={content?.primaryButtonColor || '#ffffff'}
                  onChange={(color) => updateContent('primaryButtonColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Text Color</label>
                <ColorPicker
                  color={content?.primaryButtonTextColor || '#1e40af'}
                  onChange={(color) => updateContent('primaryButtonTextColor', color)}
                />
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Secondary Button Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Secondary Button</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showSecondaryButton !== false}
              onChange={(e) => updateContent('showSecondaryButton', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Secondary Button</span>
          </label>
        </div>
        
        {content?.showSecondaryButton !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <input
                type="text"
                value={content?.secondaryButtonText || 'Contact Us'}
                onChange={(e) => updateContent('secondaryButtonText', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Link</label>
              <input
                type="text"
                value={content?.secondaryButtonLink || 'tel:+18333333101'}
                onChange={(e) => updateContent('secondaryButtonLink', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                type="text"
                value={content?.phoneNumber || '+18333333101'}
                onChange={(e) => updateContent('phoneNumber', e.target.value)}
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">
                Used if the button links to a phone number
              </p>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.showPhoneIcon !== false}
                  onChange={(e) => updateContent('showPhoneIcon', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Show Phone Icon</span>
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Color</label>
                <ColorPicker
                  color={content?.secondaryButtonColor || '#b45309'}
                  onChange={(color) => updateContent('secondaryButtonColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Text Color</label>
                <ColorPicker
                  color={content?.secondaryButtonTextColor || '#ffffff'}
                  onChange={(color) => updateContent('secondaryButtonTextColor', color)}
                />
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Spacing & Animation Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Spacing & Animation</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Top Padding</label>
            <input
              type="text"
              value={content?.paddingTop || '8rem'}
              onChange={(e) => updateContent('paddingTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 8rem, 128px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Padding</label>
            <input
              type="text"
              value={content?.paddingBottom || '10rem'}
              onChange={(e) => updateContent('paddingBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 10rem, 160px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bottom Margin</label>
            <input
              type="text"
              value={content?.marginBottom || '5rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 5rem, 80px"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.animationEnabled !== false}
              onChange={(e) => updateContent('animationEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Animation</span>
          </label>
        </div>
        
        {content?.animationEnabled !== false && (
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content?.animationStagger !== false}
                onChange={(e) => updateContent('animationStagger', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">Stagger Animation (Elements fade in one after another)</span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default CtaForm;







// //very simple
// // src/components/admin/page-management/section-forms/CtaForm.js
// import React from 'react';
// import ImageUploader from '../ImageUploader';

// const CtaForm = ({ content, updateContent, pageSlug }) => {
//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Call to Action Section</h3>
      
//       {/* Content Settings */}
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Title</label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.ctaTitle || ''}
//           onChange={(e) => updateContent('ctaTitle', e.target.value)}
//         />
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Text</label>
//         <textarea
//           className="w-full p-2 border rounded"
//           rows="3"
//           value={content?.ctaText || ''}
//           onChange={(e) => updateContent('ctaText', e.target.value)}
//         ></textarea>
//       </div>
      
//       {/* Background Image */}
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Background Image URL</label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.backgroundImageUrl || ''}
//           onChange={(e) => updateContent('backgroundImageUrl', e.target.value)}
//         />
//         {pageSlug && (
//           <ImageUploader 
//             onImageUpload={(path) => updateContent('backgroundImageUrl', path)} 
//             pageSlug={pageSlug} 
//           />
//         )}
        
//         {content?.backgroundImageUrl && (
//           <div className="mt-2 p-2 border rounded">
//             <img 
//               src={content.backgroundImageUrl} 
//               alt="CTA background"
//               className="max-w-full h-auto max-h-32 mx-auto"
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Overlay Settings */}
//       <div className="mb-4 p-3 border rounded bg-gray-50">
//         <h4 className="font-medium mb-2">Background Overlay</h4>
        
//         <div className="flex items-center mb-2">
//           <input
//             type="checkbox"
//             className="mr-2"
//             checked={content?.overlay?.enabled || false}
//             onChange={(e) => updateContent('overlay', {
//               ...content.overlay,
//               enabled: e.target.checked
//             })}
//           />
//           <label className="text-sm">Enable overlay</label>
//         </div>
        
//         {content?.overlay?.enabled && (
//           <>
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Overlay Color</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 placeholder="rgba(0,0,0,0.5) or #000000"
//                 value={content?.overlay?.color || 'rgba(0,0,0,0.5)'}
//                 onChange={(e) => updateContent('overlay', {
//                   ...content.overlay,
//                   color: e.target.value
//                 })}
//               />
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Blend Mode</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={content?.overlay?.blendMode || 'normal'}
//                 onChange={(e) => updateContent('overlay', {
//                   ...content.overlay,
//                   blendMode: e.target.value
//                 })}
//               >
//                 <option value="normal">Normal</option>
//                 <option value="multiply">Multiply</option>
//                 <option value="screen">Screen</option>
//                 <option value="overlay">Overlay</option>
//                 <option value="darken">Darken</option>
//                 <option value="lighten">Lighten</option>
//                 <option value="color-dodge">Color Dodge</option>
//                 <option value="color-burn">Color Burn</option>
//                 <option value="hard-light">Hard Light</option>
//                 <option value="soft-light">Soft Light</option>
//                 <option value="difference">Difference</option>
//                 <option value="exclusion">Exclusion</option>
//               </select>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* Button Settings */}
//       <div className="mb-4 p-3 border rounded bg-gray-50">
//         <h4 className="font-medium mb-2">Button Settings</h4>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Button Text</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.ctaButtonText || ''}
//             onChange={(e) => updateContent('ctaButtonText', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Button Link</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             value={content?.ctaButtonLink || ''}
//             onChange={(e) => updateContent('ctaButtonLink', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Button Style</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.buttonStyle || 'primary'}
//             onChange={(e) => updateContent('buttonStyle', e.target.value)}
//           >
//             <option value="primary">Primary</option>
//             <option value="secondary">Secondary</option>
//             <option value="outline">Outline</option>
//             <option value="link">Link</option>
//           </select>
//         </div>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Button Size</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.buttonSize || 'medium'}
//             onChange={(e) => updateContent('buttonSize', e.target.value)}
//           >
//             <option value="small">Small</option>
//             <option value="medium">Medium</option>
//             <option value="large">Large</option>
//           </select>
//         </div>
        
//         <div className="flex items-center mb-2">
//           <input
//             type="checkbox"
//             className="mr-2"
//             checked={content?.openInNewTab || false}
//             onChange={(e) => updateContent('openInNewTab', e.target.checked)}
//           />
//           <label className="text-sm">Open link in new tab</label>
//         </div>
//       </div>
      
//       {/* Layout Settings */}
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Content Alignment</label>
//         <select
//           className="w-full p-2 border rounded"
//           value={content?.alignment || 'center'}
//           onChange={(e) => updateContent('alignment', e.target.value)}
//         >
//           <option value="left">Left</option>
//           <option value="center">Center</option>
//           <option value="right">Right</option>
//         </select>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Content Width</label>
//         <select
//           className="w-full p-2 border rounded"
//           value={content?.contentWidth || 'medium'}
//           onChange={(e) => updateContent('contentWidth', e.target.value)}
//         >
//           <option value="narrow">Narrow</option>
//           <option value="medium">Medium</option>
//           <option value="wide">Wide</option>
//           <option value="full">Full Width</option>
//         </select>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium mb-1">Vertical Padding</label>
//         <select
//           className="w-full p-2 border rounded"
//           value={content?.verticalPadding || 'medium'}
//           onChange={(e) => updateContent('verticalPadding', e.target.value)}
//         >
//           <option value="small">Small</option>
//           <option value="medium">Medium</option>
//           <option value="large">Large</option>
//           <option value="xlarge">Extra Large</option>
//         </select>
//       </div>
//     </div>
//   );
// };

// export default CtaForm;





